import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import FixQueueBar from './FixQueueBar';
import FixTrackingCard from './FixTrackingCard';
import ServicePagesCard from './ServicePagesCard';
import { RefreshCw, Hammer, TrendingUp, TrendingDown, Sparkles, Copy, ExternalLink, ChevronDown, ChevronUp, CheckCircle, Send, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/seo/rankings`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });
const fmtWeek = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const pct = (v) => `${(v * 100).toFixed(1)}%`;

const Delta = ({ cur, prev, invert, testId }) => {
  if (cur == null || prev == null) return null;
  const diff = cur - prev;
  const good = invert ? diff < 0 : diff > 0;
  if (diff === 0) return <span className="text-xs text-gray-500" data-testid={testId}>no change</span>;
  const Icon = (invert ? diff < 0 : diff > 0) ? TrendingUp : TrendingDown;
  const label = prev ? `${diff > 0 ? '+' : ''}${invert ? diff.toFixed(1) : Math.round((diff / prev) * 100) + '%'}` : `+${diff}`;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${good ? 'bg-green-100 text-green-700' : 'bg-rose-100 text-rose-700'}`} data-testid={testId}>
      <Icon className="w-3 h-3" /> {label} vs last week
    </span>
  );
};

const StatCard = ({ label, value, cur, prev, invert, testId }) => (
  <div className="bg-white rounded-lg shadow p-4" data-testid={testId}>
    <p className="text-xs text-gray-500 uppercase">{label} · last full week</p>
    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    <div className="mt-1"><Delta cur={cur} prev={prev} invert={invert} testId={`${testId}-delta`} /></div>
  </div>
);

const ChartCard = ({ title, children, testId }) => (
  <div className="bg-white rounded-lg shadow p-4" data-testid={testId}>
    <h4 className="font-semibold text-gray-900 mb-3 text-sm">{title}</h4>
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
    </div>
  </div>
);

const WeeklyCharts = ({ data }) => {
  const rows = data.weeks.map((w) => ({ ...w, label: fmtWeek(w.week_start) }));
  const { current: c, previous: p } = data;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Impressions" value={c?.impressions ?? '—'} cur={c?.impressions} prev={p?.impressions} testId="rank-stat-impressions" />
        <StatCard label="Clicks" value={c?.clicks ?? '—'} cur={c?.clicks} prev={p?.clicks} testId="rank-stat-clicks" />
        <StatCard label="Avg position" value={c?.position ?? '—'} cur={c?.position} prev={p?.position} invert testId="rank-stat-position" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <ChartCard title="Weekly impressions" testId="rank-chart-impressions">
          <BarChart data={rows}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} width={36} /><Tooltip /><Bar dataKey="impressions" fill="#A7468A" radius={[4, 4, 0, 0]} /></BarChart>
        </ChartCard>
        <ChartCard title="Weekly clicks" testId="rank-chart-clicks">
          <BarChart data={rows}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} width={30} allowDecimals={false} /><Tooltip /><Bar dataKey="clicks" fill="#BC516F" radius={[4, 4, 0, 0]} /></BarChart>
        </ChartCard>
        <ChartCard title="Average position (lower is better)" testId="rank-chart-position">
          <LineChart data={rows}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="label" fontSize={11} /><YAxis reversed fontSize={11} width={30} domain={[1, 'dataMax']} /><Tooltip /><Line type="monotone" dataKey="position" stroke="#1E2A44" strokeWidth={2} dot={{ r: 3, fill: '#BC516F' }} /></LineChart>
        </ChartCard>
      </div>
    </div>
  );
};

const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };

const devBrief = (k, plan) => `SEO fix for ${k.page}\nTarget search: "${k.query}" (position ${k.position}, ${k.impressions} impressions)\n\nNew title: ${plan.title}\nNew meta description: ${plan.meta}\n\nSections to add:\n${(plan.h2s || []).map((x) => `- ${x}`).join('\n')}\n\nContent gaps:\n${(plan.content_gaps || []).map((x) => `- ${x}`).join('\n')}\n\nInternal link anchors:\n${(plan.internal_links || []).map((x) => `- ${x}`).join('\n')}`;

const PlanActions = ({ k, plan, applied, applying, onApply, onMarkBuilt, testId }) => (
  <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[#A7468A]/15">
    {k.is_blog ? (
      applied ? (
        <span className="inline-flex items-center gap-1.5 text-green-700 font-semibold" data-testid={`${testId}-applied`}><CheckCircle className="w-4 h-4" /> Live on the blog post</span>
      ) : (
        <button onClick={onApply} disabled={applying} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1E2A44] text-white text-xs font-semibold hover:bg-[#A7468A] disabled:opacity-60" data-testid={`${testId}-apply`}>
          {applying ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Apply title + meta to the blog post
        </button>
      )
    ) : (
      <>
        <button onClick={() => copy(devBrief(k, plan))} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1E2A44] text-white text-xs font-semibold hover:bg-[#A7468A]" data-testid={`${testId}-copy-dev`}>
          <Copy className="w-3.5 h-3.5" /> Copy and send to developer
        </button>
        {applied ? (
          <span className="inline-flex items-center gap-1.5 text-green-700 font-semibold text-xs" data-testid={`${testId}-built`}><CheckCircle className="w-4 h-4" /> Built, tracking started</span>
        ) : (
          <button onClick={onMarkBuilt} disabled={applying} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#1E2A44] text-[#1E2A44] text-xs font-semibold hover:bg-[#1E2A44] hover:text-white disabled:opacity-60" data-testid={`${testId}-mark-built`}>
            <Hammer className="w-3.5 h-3.5" /> Mark as built (start tracking)
          </button>
        )}
        <span className="text-xs text-gray-500">Main site page: changes go in the site code. Mark as built once they&rsquo;re live.</span>
      </>
    )}
  </div>
);

const PlanView = ({ plan, testId, children }) => (
  <div className="mt-3 rounded-lg border border-[#A7468A]/20 bg-[#A7468A]/5 p-4 text-sm space-y-3" data-testid={testId}>
    <p><b>Intent:</b> {plan.intent}</p>
    <p><b>Verdict:</b> {plan.verdict}</p>
    {[['New title', plan.title], ['New meta description', plan.meta]].map(([l, v]) => (
      <div key={l} className="flex items-start gap-2">
        <div className="flex-1"><b>{l}</b> <span className="text-gray-500">({v.length}c)</span><div className="mt-0.5 bg-white rounded border px-2 py-1">{v}</div></div>
        <button onClick={() => copy(v)} className="mt-5 text-gray-500 hover:text-gray-900" aria-label={`Copy ${l}`}><Copy className="w-4 h-4" /></button>
      </div>
    ))}
    {[['Sections to add', plan.h2s], ['Content gaps', plan.content_gaps], ['Internal link anchors', plan.internal_links]].map(([l, arr]) => (
      <div key={l}><b>{l}</b><ul className="list-disc pl-5 mt-1 space-y-0.5">{(arr || []).map((x) => <li key={x}>{x}</li>)}</ul></div>
    ))}
    {children}
  </div>
);

const KeywordRow = ({ k, i, checked, onCheck, onChanged }) => {
  const [plan, setPlan] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(!!k.applied);
  const [applying, setApplying] = useState(false);
  const apply = async () => {
    setApplying(true);
    try {
      await axios.post(`${API}/apply-fix-plan`, { query: k.query, page: k.page }, { headers: authHeaders() });
      setApplied(true);
      toast.success('New title and meta are live. Bing and IndexNow have been pinged.');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally { setApplying(false); }
  };
  const markBuilt = async () => {
    setApplying(true);
    try {
      await axios.post(`${API}/tracking/mark-built`, { query: k.query, page: k.page }, { headers: authHeaders() });
      setApplied(true);
      toast.success('Tracking started. Results in about a month.');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start tracking');
    } finally { setApplying(false); }
  };
  const getPlan = async (refresh = false) => {
    setLoading(true); setOpen(true);
    try {
      const res = await axios.post(`${API}/fix-plan`, { query: k.query, page: k.page, position: k.position, impressions: k.impressions, ctr: k.ctr, refresh }, { headers: authHeaders(), timeout: 90000 });
      setPlan(res.data.plan);
      if (refresh) setApplied(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI fix plan failed');
    } finally { setLoading(false); }
  };
  const band = { 'page-one': 'bg-green-100 text-green-700', 'page-two': 'bg-amber-100 text-amber-700', 'page-three': 'bg-gray-100 text-gray-600' }[k.band];
  return (
    <div className="border-b border-gray-100 py-4" data-testid={`rank-kw-${i}`}>
      <div className="flex flex-wrap items-start gap-3">
        <input type="checkbox" checked={checked} onChange={onCheck} disabled={applied} className="mt-1 w-4 h-4 accent-[#A7468A]" aria-label={`Select ${k.query}`} data-testid={`rank-kw-${i}-check`} />
        <div className="flex-1 min-w-[220px]">
          <p className="font-semibold text-gray-900">{k.query}{k.queued && !applied && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#A7468A]/10 text-[#A7468A] px-2 py-0.5 rounded-full" data-testid={`rank-kw-${i}-queued-badge`}>In build brief</span>}{applied && <span className="ml-2 text-[10px] uppercase tracking-wider bg-green-100 text-green-700 px-2 py-0.5 rounded-full" data-testid={`rank-kw-${i}-applied-badge`}>Applied</span>}</p>
          <a href={k.page} target="_blank" rel="noopener noreferrer" className="text-xs text-[#A7468A] hover:underline inline-flex items-center gap-1 break-all">{k.page.replace('https://audioandco.com', '') || '/'} <ExternalLink className="w-3 h-3" /></a>
        </div>
        <div className="flex gap-4 text-sm text-gray-700">
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${band}`} data-testid={`rank-kw-${i}-position`}>#{k.position}</span>
          <span>{k.impressions} impr</span><span>{k.clicks} clicks</span><span>{pct(k.ctr)} CTR</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => (plan ? setOpen(!open) : getPlan())} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-[#A7468A] to-[#BC516F] text-white disabled:opacity-60" data-testid={`rank-kw-${i}-ai`}>
            {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {plan ? (open ? 'Hide fix plan' : 'Show fix plan') : (k.has_plan ? 'View fix plan' : 'AI fix plan')}
            {plan && (open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
          </button>
          {plan && open && <button onClick={() => getPlan(true)} className="text-xs text-gray-500 hover:text-gray-900" data-testid={`rank-kw-${i}-regen`}>Regenerate</button>}
        </div>
      </div>
      <p className="text-sm text-gray-600 mt-2" data-testid={`rank-kw-${i}-tip`}>{k.tip}</p>
      {open && plan && !loading && (
        <PlanView plan={plan} testId={`rank-kw-${i}-plan`}>
          <PlanActions k={k} plan={plan} applied={applied} applying={applying} onApply={apply} onMarkBuilt={markBuilt} testId={`rank-kw-${i}`} />
        </PlanView>
      )}
      {open && loading && <p className="text-xs text-gray-500 mt-2">Writing a fix plan, about 15 seconds…</p>}
    </div>
  );
};

export const RankingsPanel = () => {
  const [weekly, setWeekly] = useState(null);
  const [opps, setOpps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tracking, setTracking] = useState([]);
  const [selected, setSelected] = useState({});
  const loadTracking = useCallback(async () => {
    try {
      const t = await axios.get(`${API}/tracking`, { headers: authHeaders(), timeout: 60000 });
      setTracking(t.data.items);
    } catch (e) { /* tracking is optional */ }
  }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, o] = await Promise.all([
        axios.get(`${API}/weekly?weeks=16`, { headers: authHeaders(), timeout: 60000 }),
        axios.get(`${API}/opportunities?days=90`, { headers: authHeaders(), timeout: 60000 }),
      ]);
      setWeekly(w.data); setOpps(o.data);
      loadTracking();
    } catch (e) {
      toast.error('Could not load Search Console data');
    } finally { setLoading(false); }
  }, [loadTracking]);
  useEffect(() => { load(); }, [load]);
  const keyOf = (k) => `${k.query}|${k.page}`;
  const toggle = (k) => setSelected((s) => { const n = { ...s }; if (n[keyOf(k)]) delete n[keyOf(k)]; else n[keyOf(k)] = { query: k.query, page: k.page, position: k.position, impressions: k.impressions, ctr: k.ctr }; return n; });
  const [sending, setSending] = useState(false);
  const sendEmail = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/send-email`, {}, { headers: authHeaders(), timeout: 60000 });
      res.data.sent ? toast.success('Rankings email sent to jo@audioandco.com') : toast.error(res.data.error || 'Email not sent');
    } catch (e) {
      toast.error('Email failed');
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-rankings-tab">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Ranking Progress</h2>
          <p className="text-gray-600 text-sm">Google Search Console, last 16 weeks. Data runs about 2 days behind. A summary is emailed every Monday at 8am.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={sendEmail} disabled={sending} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm hover:bg-gray-50 disabled:opacity-60" data-testid="rankings-send-email">
            <Mail className="w-4 h-4" /> {sending ? 'Sending…' : 'Email me this week'}
          </button>
          <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm hover:bg-gray-50" data-testid="rankings-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>
      {weekly && !weekly.ok && <p className="text-rose-700 text-sm" data-testid="rankings-error">{weekly.error}</p>}
      {weekly?.ok && <WeeklyCharts data={weekly} />}
      <ServicePagesCard />
      <FixTrackingCard items={tracking} />
      <div className="sticky top-2 z-20"><FixQueueBar selected={Object.values(selected)} onClear={() => setSelected({})} onDone={load} /></div>
      <div className="bg-white rounded-lg shadow p-5" data-testid="rankings-quick-wins">
        <h3 className="text-lg font-bold text-gray-900">Quick-win keywords</h3>
        <p className="text-sm text-gray-600">Searches where you rank between positions 4 and 30 (last 90 days), sorted by how close they are to paying off. Tick several and press Build to do them in one go.</p>
        {opps?.ok && opps.keywords.length === 0 && <p className="text-sm text-gray-500 mt-4" data-testid="rankings-quick-wins-empty">No keywords in range yet.</p>}
        {opps?.ok && opps.keywords.map((k, i) => <KeywordRow key={`${k.query}-${k.page}`} k={k} i={i} checked={!!selected[keyOf(k)]} onCheck={() => toggle(k)} onChanged={loadTracking} />)}
        {loading && !opps && <p className="text-sm text-gray-500 mt-4">Loading…</p>}
      </div>
    </div>
  );
};

export default RankingsPanel;
